import { Alert, Button } from "@material-tailwind/react";
import moment from "moment";
import React, { Fragment } from "react";
import toast from "react-hot-toast";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import LoadingSpinner from "../../Components/UI/LoadingSpinner";
import { usePlan } from "../../Context/PlanContext";
import { useUserStore } from "../../Context/store";
import {
  convertEpochToLocalTime,
  convertSubCurrency,
  convertSubcurrencyToMain,
} from "../../Utils/Helper";
import {
  useCreateSubscription,
  useGetCustomerDetails,
  useGetPaymentMethods,
  useGetProductDetails,
  useGetUpcomingInvoiceDetails,
} from "../../Web-Hooks/Subscription/use-subscription";
import PaymentMethods from "./ChangePaymentMethod";
import CouponCodePage from "./CouponCodePage";
import RenderCalculationUI from "./RenderCalculationUI";

const UpdateCheckOutPage = () => {
  //* State
  const [appliedCoupon, setAppliedCoupon] = React.useState(null);

  //* Hooks
  const location = useLocation();
  const { features, currentPlanName } = location.state;
  const { subscribedPlan } = usePlan();
  const { user: loggedInUser } = useUserStore();
  const [search] = useSearchParams();

  //* Get Values
  const stripeProductId = search.get("productId");
  const amount = search.get("amount");
  const currencyInUnit = convertSubCurrency(amount);
  const plan = search.get("plan");
  const type = search.get("type");
  const planId = search.get("planId");

  //* API Hooks
  const { data: product } = useGetProductDetails(stripeProductId, type);
  const { data: customer } = useGetCustomerDetails(subscribedPlan?.customerId);
  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } =
    useGetPaymentMethods(subscribedPlan?.customerId);
  const { mutateAsync: createSubscription, isPending: isUpdating } =
    useCreateSubscription();

  const findProduct = product?.find(
    (prod) => prod?.productId === stripeProductId
  );
  const findPrice = findProduct?.prices?.find(
    (price) =>
      price.recurringInterval === type && price.priceAmount === currencyInUnit
  );

  const stripeSubscription = subscribedPlan?.stripeJson;

  const { data, isLoading } = useGetUpcomingInvoiceDetails(
    subscribedPlan?.customerId,
    findPrice?.priceId,
    stripeSubscription?.items.data[0].id,
    stripeSubscription?.id
  );
  const nextBillingCycleDate = moment().add(1, type).format("MMMM D, YYYY");
  const navigate = useNavigate();
  const newFeatures = features?.filter((feature) => feature?.included);
  const billingPeriodEndDate = moment(
    convertEpochToLocalTime(data?.upcomingInvoice?.lines?.data[1]?.period?.end)
  ).format("MMMM D, YYYY");

  const isFreeStripePlanCancelSchedule =
    stripeSubscription?.cancel_at_period_end &&
    stripeSubscription?.status === "trialing";
  const isStripePlanCancelled =
    stripeSubscription?.cancel_at_period_end &&
    stripeSubscription?.status === "canceled";
  const isActivePlanCancelSchedule =
    stripeSubscription?.cancel_at_period_end &&
    stripeSubscription?.status === "active";

  const isDownGrade = data?.isPlanDownGraded;

  const handleSubmit = async () => {
    let payload;

    if (appliedCoupon?.code) {
      payload = {
        promoCode: appliedCoupon?.code,
      };
    }

    try {
      const response = await createSubscription({
        customerEmail: loggedInUser?.email,
        customerName: loggedInUser?.userName,
        priceId: findPrice?.priceId,
        ...payload,
      });
      const total_amount = appliedCoupon
        ? appliedCoupon?.invoiceJson?.amount_remaining / 100
        : amount;

      switch (response?.status) {
        case "not_started":
          Swal.fire({
            title: "Plan Downgrade Scheduled",
            html: `
              <p>Your plan downgrade from <strong>${currentPlanName}</strong> to <strong>${plan}</strong> has been scheduled.</p>
              <p>The change will take effect after your current plan expires on <strong>${billingPeriodEndDate}</strong>.</p>
              <p>Until then, you'll continue to enjoy all the benefits of your current plan.</p>
              <p>If you have any questions, please feel free to <a href="/contact-support">contact support</a>.</p>
            `,
            icon: "info",
            confirmButtonText: "Okay, thanks!",
          }).then((result) => {
            if (result.isConfirmed) {
              navigate("/feed-public");
            }
          });
          break;
        case "active":
        case "trialing":
          navigate(
            `/payment-success?amount=${total_amount}&plan=${plan}&type=${type}&planId=${planId}&subType=${
              isDownGrade ? "downgrade" : "upgrade"
            }`,
            {
              state: { amount: total_amount, plan, type },
              replace: true,
            }
          );
          break;
        default:
          navigate(
            `/payment-failed?amount=${total_amount}&plan=${plan}&type=${type}&planId=${planId}`,
            {
              state: { amount: total_amount, plan, type },
              replace: true,
            }
          );
          break;
      }
    } catch (error) {
      const errorMessageBackend = error?.response?.data?.message || error?.response?.data?.error;
      toast.error(errorMessageBackend ? errorMessageBackend : error.message);
    }
  };

  const renderError = () => {
    if (paymentMethods?.length === 0) {
      return (
        <Fragment>
          <Alert variant="filled" className="bg-secondary">
            <span>
              You don't have any payment method added. Please add a payment
              method to continue.
            </span>
          </Alert>
        </Fragment>
      );
    } else if (paymentMethods?.length > 0 && !defaultMethod) {
      return (
        <Fragment>
          <Alert variant="filled" className="bg-secondary">
            <span>
              You don't have any default payment method added. if you want to
              add a default payment method for future use, please choose the
              card and click on set as default.
            </span>
          </Alert>
        </Fragment>
      );
    } else {
      return null;
    }
  };

  const handleCouponApplied = (couponCode, validationResult) => {
    if (validationResult?.isValid) {
      setAppliedCoupon({
        code: couponCode,
        discountAmount: validationResult.discountAmount,
        discountType: validationResult.discountType,
        invoiceJson: validationResult?.invoiceJson,
      });
    } else {
      setAppliedCoupon(null);
    }
  };

  const renderDownGradeUI = () => {
    return (
      <div className="flex flex-col gap-6 items-stretch justify-center">
        {(isActivePlanCancelSchedule || isFreeStripePlanCancelSchedule) && (
          <p className="text-base font-normal text-debatePalette-title text-pretty">
            Your subscription is currently scheduled to cancel on{" "}
            {moment(
              convertEpochToLocalTime(stripeSubscription?.current_period_end)
            ).format("MMMM D, YYYY")}
            . By confirming, your updated subscription will be renewed with the
            following details.
          </p>
        )}
        <p className="text-base font-normal text-debatePalette-title">
          Your subscription will be updated at the end of your current billing
          period on{" "}
          {moment(
            convertEpochToLocalTime(stripeSubscription?.current_period_end)
          ).format("MMMM D, YYYY")}
          . You will have access to your current subscription's features until
          then.
        </p>
        <div className="grid grid-cols-[1fr_auto] gap-3 text-debatePalette-title text-base font-semibold">
          <p>
            What you'll pay {type === "month" ? "monthly" : "yearly"} starting
          </p>
          <p>${amount}</p>
        </div>

        <div className="text-debatePalette-title text-base font-semibold">
          {moment(
            convertEpochToLocalTime(stripeSubscription?.current_period_end)
          ).format("MMMM D, YYYY")}
        </div>

        <hr className="bg-gray-500 border-debatePalette-hr border-1 dark:bg-gray-700" />
        {!isFreeStripePlanCancelSchedule && !isStripePlanCancelled && (
          <RenderCalculationUI
            upcomingInvoice={data?.upcomingInvoice}
            oldPlan={{
              planName: currentPlanName,
              priceId: stripeSubscription?.items?.data[0]?.plan?.id,
            }}
            newPlan={{
              planName: plan,
              priceId: findPrice?.priceId,
            }}
          />
        )}

        <Button
          className="bg-primary text-secondary flex justify-center gap-3 items-center text-[14px] font-bold"
          loading={isUpdating}
          onClick={handleSubmit}
          disabled={!paymentMethods?.length || isUpdating}
        >
          Confirm
        </Button>
        {renderError()}
      </div>
    );
  };

  const renderUpGradeUI = () => {
    return (
      <div className="flex flex-col gap-6 items-stretch justify-center">
        {(isActivePlanCancelSchedule || isFreeStripePlanCancelSchedule) && (
          <p className="text-base font-normal text-debatePalette-title text-pretty">
            Your subscription is currently scheduled to cancel on{" "}
            {moment(
              convertEpochToLocalTime(stripeSubscription?.current_period_end)
            ).format("MMMM D, YYYY")}
            . By confirming, your updated subscription will be renewed with the
            following details.
          </p>
        )}
        <div className="grid grid-cols-[1fr_auto] gap-1 text-debatePalette-title text-base font-semibold">
          <p className="text-base font-normal">
            What you'll pay {type === "month" ? "monthly" : "yearly"} starting
          </p>
          <p className="text-base font-normal">
            {convertSubcurrencyToMain(
              stripeSubscription?.cancel_at_period_end
                ? stripeSubscription?.currency || findPrice?.currency
                : data?.upcomingInvoice?.currency || findPrice?.currency,
              convertSubCurrency(amount || findPrice?.priceAmount)
            )}
          </p>

          {/* <PencilSquareIcon className="h-6 w-6" /> */}
        </div>

        {/* Display Coupon Details */}
        {appliedCoupon && (
          <div className="space-y-2 text-debatePalette-title text-base">
            {/* Discount */}
            <div className="flex justify-between items-start">
              Coupon Applied ({appliedCoupon.code} -
              {appliedCoupon.discountType === "percentage"
                ? ` ${appliedCoupon.discountAmount}%`
                : ` $${(appliedCoupon.discountAmount / 100).toFixed(2)}`}
              )
              <span className="text-end">
                -$
                {appliedCoupon.discountType === "fixed"
                  ? (appliedCoupon.discountAmount / 100).toFixed(2)
                  : (
                      appliedCoupon?.invoiceJson?.total_discount_amounts[0]
                        .amount / 100
                    ).toFixed(2)}
              </span>
            </div>
          </div>
        )}

        <p className="text-base font-normal">{nextBillingCycleDate}</p>

        <hr className="bg-gray-500 border-debatePalette-hr border-1 dark:bg-gray-700" />
        {!isFreeStripePlanCancelSchedule && !isStripePlanCancelled && (
          <RenderCalculationUI
            upcomingInvoice={data?.upcomingInvoice}
            oldPlan={{
              planName: currentPlanName,
              priceId: stripeSubscription?.items?.data[0]?.plan?.id,
            }}
            newPlan={{
              planName: plan,
              priceId: findPrice?.priceId,
            }}
          />
        )}

        <div className="grid grid-cols-[1fr_auto] gap-1 text-debatePalette-title text-base font-semibold">
          <p className="text-base font-semibold">Amount due today</p>
          <p className="text-lg font-bold">
            {appliedCoupon
              ? convertSubcurrencyToMain(
                  appliedCoupon?.invoiceJson?.currency,
                  appliedCoupon?.invoiceJson?.amount_remaining
                )
              : convertSubcurrencyToMain(
                  data?.upcomingInvoice?.currency || findPrice?.currency,
                  data?.upcomingInvoice?.amount_due || findPrice?.priceAmount
                )}
          </p>
        </div>

        {/* //* COUPON CODE FIELD */}
        <CouponCodePage
          onCouponApplied={handleCouponApplied}
          subscribedPlan={subscribedPlan}
          findPrice={findPrice}
          stripeSubscription={stripeSubscription}
        />

        <Button
          className="bg-primary text-secondary flex justify-center gap-3 items-center text-[14px] font-bold"
          loading={isUpdating}
          onClick={handleSubmit}
          disabled={!paymentMethods?.length || isUpdating}
        >
          Confirm and pay
        </Button>
        {renderError()}
        <div className="">
          <h4 className="text-debatePalette-title font-semibold text-lg">
            New Features:
          </h4>
          <ul className="mt-4 grid grid-cols-2 gap-1 space-y-3 text-base text-debatePalette-title">
            {newFeatures?.map((feature, index) => (
              <li key={index} className="flex items-center">
                <span className="inline-block w-3 h-3 mr-2 bg-secondary rounded-full text-debatePalette-title"></span>
                {feature?.text}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  };

  const defaultMethod =
    stripeSubscription?.default_payment_method ||
    customer?.invoice_settings?.default_payment_method;

  if (isLoading || !findPrice) {
    return (
      <div className="min-h-screen flex flex-col gap-2 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 flex flex-col gap-2 items-center justify-start">
      <h3 className="text-3xl md:text-5xl font-bold text-debatePalette-title">
        Confirm your updates
      </h3>
      <div className="grid grid-cols-1 w-full lg:w-4/5 2xl:w-3/5  md:grid-cols-2 gap-4">
        <div className="rounded-lg flex flex-col justify-start h-full w-full bg-gray-50 p-3">
          <div className="px-2 sm:px-4">
            <div className="flex flex-col gap-6">
              <div className="flex justify-between text-gray-600 text-lg">
                <span className="text-debatePalette-title text-2xl md:text-4xl font-semibold">
                  {plan}
                </span>
              </div>
              {isDownGrade ? renderDownGradeUI() : renderUpGradeUI()}
            </div>
          </div>
        </div>
        <div className="rounded-lg flex flex-col justify-start h-full w-full bg-gray-50 p-3">
          {
            <PaymentMethods
              paymentMethods={paymentMethods}
              isPaymentMethodsLoading={isPaymentMethodsLoading}
              customerId={subscribedPlan?.customerId}
              defaultMethod={defaultMethod}
              subscriptionId={stripeSubscription?.id}
              clientSecret={data?.clientSecret}
            />
          }
        </div>
      </div>
    </div>
  );
};

export default UpdateCheckOutPage;
